import { useQueryClient } from "@tanstack/react-query";
import { useState, useSyncExternalStore } from "react";

import type { AuthClient } from "../auth/authClient";
import { PopupBlockedError } from "../auth/errors";
import { useGoToMicrosoft } from "./goToMicrosoft";

const button =
  "h-9 cursor-pointer rounded-pill bg-cta-bg px-4 text-[13px] font-bold text-cta-fg disabled:cursor-not-allowed disabled:opacity-50";
const quiet =
  "h-9 cursor-pointer rounded-pill border border-line bg-card px-4 text-[13px] font-medium text-text hover:bg-brand-tint";

/**
 * Says when the sign-in needs renewing, and renews it in a popup when the tech
 * chooses, so the page, and anything typed on it, stays (#252; ADR 0093). An
 * open tab never leaves for Microsoft by itself. If the browser blocks the
 * popup, renewing in this tab is offered, saying what that costs.
 */
export function RenewalBanner({ auth }: { auth: AuthClient }) {
  const queryClient = useQueryClient();
  const needed = useSyncExternalStore(
    (onChange) => auth.onRenewalNeeded(onChange),
    () => auth.renewalNeeded,
  );
  const [blocked, setBlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const thisTab = useGoToMicrosoft();

  if (!needed) {
    return null;
  }

  const renew = () => {
    setBusy(true);
    // Straight from the click, so the browser lets the popup open.
    auth.renewSession().then(
      () => {
        setBusy(false);
        setBlocked(false);
        void queryClient.invalidateQueries();
      },
      (error: unknown) => {
        setBusy(false);
        // Closed before signing in: the banner stays, to try again.
        setBlocked(error instanceof PopupBlockedError);
      },
    );
  };

  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-3 rounded-panel border border-status-needs-client-line bg-status-needs-client-bg px-4 py-3 text-sm text-status-needs-client-fg"
    >
      <p className="min-w-0 flex-1">
        {blocked ? (
          <>
            Your browser blocked the sign-in window. Allow pop-ups for this site and try again, or
            continue in this tab: anything typed and not yet saved will be lost.
          </>
        ) : (
          <>Your sign-in needs renewing. Nothing typed here is lost: continue in a small sign-in window.</>
        )}
        {thisTab.failed === null ? null : <span className="mt-1 block font-semibold">{thisTab.failed}</span>}
      </p>
      <button type="button" className={button} disabled={busy} onClick={renew}>
        {blocked ? "Try again" : "Continue"}
      </button>
      {blocked ? (
        <button
          type="button"
          className={quiet}
          onClick={() => {
            thisTab.go(() => auth.renewInThisTab());
          }}
        >
          Continue in this tab
        </button>
      ) : null}
    </div>
  );
}
